// iter717 — Reconciliation Cockpit. Single page that lists every
// unmatched bank-statement transaction across the chosen date range
// alongside its best candidate (party, branch, score, "why"). The
// bookkeeper can:
//   • Tap ✓ to instantly accept the best candidate for one row
//   • Tap ↻ to open the full Find-Match picker (10 candidates)
//   • Tick rows + "Accept all selected (best ≥ N%)" to bulk-link
// Goal: blast through 100s of un-linked Jan→today txns in one sitting.

import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { CheckCircle2, RefreshCcw, Sparkles, Loader2, Filter, AlertTriangle, ListChecks, Timer, Play, Bell, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import MatchCandidatesDialog from '@/components/banking/MatchCandidatesDialog';

const TYPE_LABEL = { sale: 'Sale', expense: 'Expense', supplier_payment: 'Supplier payment', salary_payment: 'Salary', customer_payment: 'Customer payment', sales_daily_total: 'Daily sales total' };
const TYPE_TONE = {
  sale: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  expense: 'bg-rose-50 text-rose-700 border-rose-200',
  supplier_payment: 'bg-orange-50 text-orange-700 border-orange-200',
  sales_daily_total: 'bg-violet-50 text-violet-700 border-violet-200',
};

const PAGE_SIZE = 200;

function ymdFirstOfYear() {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
}
function ymdToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function BankReconcileCockpitPage() {
  const [from, setFrom] = useState(ymdFirstOfYear());
  const [to, setTo] = useState(ymdToday());
  const [kind, setKind] = useState('all');
  const [minAmount, setMinAmount] = useState(0);
  const [threshold, setThreshold] = useState(85);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({}); // {`${stmtId}:${idx}`: bool}
  const [busy, setBusy] = useState(false);
  const [matchDialog, setMatchDialog] = useState({ open: false, statementId: null, txnIdx: null });
  const [queueBusy, setQueueBusy] = useState(false);

  // iter719 — Nightly auto-link scheduler settings + history.
  const [autoLink, setAutoLink] = useState(null);
  const [autoLinkBusy, setAutoLinkBusy] = useState(false);
  const [autoLinkRuns, setAutoLinkRuns] = useState([]);

  const loadAutoLink = async () => {
    try {
      const [{ data: s }, { data: runs }] = await Promise.all([
        api.get('/recon-cockpit/auto-link/settings'),
        api.get('/recon-cockpit/auto-link/runs?limit=5'),
      ]);
      setAutoLink(s);
      setAutoLinkRuns(Array.isArray(runs) ? runs : []);
    } catch (e) {
      console.warn('[cockpit] auto-link fetch failed:', e?.response?.data?.detail || e?.message);
    }
  };
  useEffect(() => { loadAutoLink(); }, []);

  const saveAutoLink = async () => {
    if (!autoLink) return;
    setAutoLinkBusy(true);
    try {
      const { data } = await api.put('/recon-cockpit/auto-link/settings', autoLink);
      setAutoLink(data);
      toast.success(`Auto-link ${data.enabled ? 'enabled' : 'disabled'}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setAutoLinkBusy(false);
    }
  };

  const runAutoLinkNow = async () => {
    if (!window.confirm('Run an auto-link pass right now? All candidates at or above the saved threshold will be linked immediately.')) return;
    setAutoLinkBusy(true);
    try {
      const { data } = await api.post('/recon-cockpit/auto-link/run-now');
      const n = (data.accepted || []).length;
      if (n) toast.success(`Auto-linked ${n} transaction${n === 1 ? '' : 's'} (scanned ${data.scanned || 0}).`);
      else toast.info(`No candidates above ${data.min_score}% — nothing linked.`);
      await Promise.all([load(), loadAutoLink()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setAutoLinkBusy(false);
    }
  };

  const fetchPage = (offset) => {
    const params = new URLSearchParams({ kind, min_amount: String(minAmount || 0), limit: String(PAGE_SIZE), offset: String(offset) });
    if (from) params.set('from_date', from);
    if (to) params.set('to_date', to);
    return api.get(`/recon-cockpit/unmatched?${params}`).then((r) => r.data);
  };

  const loadMore = async () => {
    if (!data?.has_more) return;
    setLoadingMore(true);
    try {
      const next = await fetchPage(data.rows.length);
      setData((d) => ({ ...next, rows: [...(d?.rows || []), ...(next.rows || [])] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setSelected({});
    try {
      setData(await fetchPage(0));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load unmatched txns');
    } finally {
      setLoading(false);
    }
  };

  const approveQueueRows = async (qRows) => {
    if (!qRows.length) return;
    setQueueBusy(true);
    try {
      const { data: res } = await api.post('/recon-cockpit/bulk-match', {
        matches: qRows.map((r) => ({
          statement_id: r.statement_id,
          txn_index: r.txn_index,
          match_type: r.best.type,
          match_id: r.best.id,
        })),
      });
      toast.success(`Approved ${res.accepted} match${res.accepted === 1 ? '' : 'es'}${res.failed?.length ? ` · ${res.failed.length} failed` : ''}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    } finally {
      setQueueBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const rows = data?.rows || [];
  const totalWithBest = useMemo(() => rows.filter((r) => r.best).length, [rows]);
  // Review queue = same rule as /recon-cockpit/review-queue (gov fee / salary, score ≥ 90), taken from the loaded rows (no second full scan).
  const queue = useMemo(() => {
    const q = rows.filter((r) => r.kind === 'debit' && r.best?.review_category && (r.best.score || 0) >= 90);
    return { rows: q, gov_fee: q.filter((r) => r.best.review_category === 'gov_fee').length, salary: q.filter((r) => r.best.review_category === 'salary').length };
  }, [rows]);
  const aboveThreshold = useMemo(
    () => rows.filter((r) => (r.best?.score || 0) >= threshold),
    [rows, threshold],
  );

  const toggleRow = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
  const selectAllAboveThreshold = () => {
    const next = {};
    for (const r of aboveThreshold) next[`${r.statement_id}:${r.txn_index}`] = true;
    setSelected(next);
  };
  const selectNone = () => setSelected({});
  const selectedRows = useMemo(
    () => rows.filter((r) => selected[`${r.statement_id}:${r.txn_index}`] && r.best),
    [rows, selected],
  );

  const acceptOne = async (row) => {
    if (!row.best) return;
    setBusy(true);
    try {
      await api.post(`/bank-statements/${row.statement_id}/manual-match`, {
        txn_index: row.txn_index,
        match_type: row.best.type,
        match_id: row.best.id,
      });
      toast.success(`Linked to ${row.best.party_name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Match failed');
    } finally {
      setBusy(false);
    }
  };

  const acceptSelected = async () => {
    if (selectedRows.length === 0) { toast.error('Pick at least one row first'); return; }
    if (!window.confirm(`Link ${selectedRows.length} transaction(s) to their best candidate? This is reversible by un-matching individually.`)) return;
    setBusy(true);
    try {
      const matches = selectedRows.map((r) => ({
        statement_id: r.statement_id,
        txn_index: r.txn_index,
        match_type: r.best.type,
        match_id: r.best.id,
      }));
      const { data } = await api.post('/recon-cockpit/bulk-match', { matches });
      const f = (data.failed || []).length;
      toast.success(`${data.accepted} linked${f ? ` · ${f} failed` : ''}`);
      if (f && data.failed[0]?.error) {
        toast.warning(`First failure: ${data.failed[0].error}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk match failed');
    } finally {
      setBusy(false);
    }
  };

  const progressPct = rows.length ? Math.round((totalWithBest / rows.length) * 100) : 0;
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const selCount = selectedRows.length;
  const aboveThreshCount = aboveThreshold.length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-700 flex items-center justify-center shadow-lg shadow-violet-200/50">
            <Sparkles size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight" data-testid="recon-cockpit-title">
              Reconciliation Cockpit
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Match every un-linked bank transaction from a date range to its supplier / expense / sale in one place. One-tap accept, bulk-accept by confidence, or pick from the top 10 candidates.
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 font-outfit">
              <Filter size={16} className="text-violet-600" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
              <div>
                <Label htmlFor="rc-from">From</Label>
                <Input id="rc-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="rc-from" />
              </div>
              <div>
                <Label htmlFor="rc-to">To</Label>
                <Input id="rc-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="rc-to" />
              </div>
              <div>
                <Label>Direction</Label>
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger data-testid="rc-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All directions</SelectItem>
                    <SelectItem value="debit">Outgoing (debits)</SelectItem>
                    <SelectItem value="credit">Incoming (credits)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="rc-min">Min amount</Label>
                <Input id="rc-min" type="number" step="0.01" value={minAmount} onChange={(e) => setMinAmount(Number(e.target.value) || 0)} data-testid="rc-min" />
              </div>
              <Button onClick={load} disabled={loading} className="rounded-xl" data-testid="rc-load">
                {loading ? <Loader2 className="animate-spin mr-1" size={14} /> : <RefreshCcw size={14} className="mr-1" />}
                {loading ? 'Loading…' : 'Reload'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* iter719 — Nightly auto-link scheduler */}
        <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap font-outfit">
              <Timer size={16} className="text-sky-600" />
              Nightly auto-link
              <Badge className="bg-sky-100 text-sky-800 border-sky-200 font-normal">Set & forget</Badge>
              {autoLink?.enabled && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Enabled</Badge>}
              {autoLink && !autoLink.enabled && <Badge variant="outline">Disabled</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Once per day at the chosen hour, the cron scans every unmatched bank txn from the last N days and auto-links anything scoring ≥ threshold. Already-linked txns are skipped. Optional email digest.
            </p>
            {autoLink && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end" data-testid="auto-link-form">
                <div className="col-span-2 md:col-span-1 flex items-center gap-2 pt-5">
                  <input
                    id="al-enabled" type="checkbox"
                    className="h-4 w-4 accent-sky-600"
                    checked={!!autoLink.enabled}
                    onChange={(e) => setAutoLink({ ...autoLink, enabled: e.target.checked })}
                    disabled={autoLinkBusy}
                    data-testid="auto-link-enabled"
                  />
                  <Label htmlFor="al-enabled" className="cursor-pointer text-sm font-medium">Enabled</Label>
                </div>
                <div>
                  <Label htmlFor="al-min-score">Min %</Label>
                  <Input
                    id="al-min-score" type="number" min={50} max={100} step={1}
                    value={autoLink.min_score}
                    onChange={(e) => setAutoLink({ ...autoLink, min_score: Number(e.target.value) || 95 })}
                    disabled={autoLinkBusy}
                    data-testid="auto-link-min-score"
                  />
                </div>
                <div>
                  <Label htmlFor="al-hour">Hour (UTC)</Label>
                  <select
                    id="al-hour"
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={autoLink.hour}
                    onChange={(e) => setAutoLink({ ...autoLink, hour: Number(e.target.value) })}
                    disabled={autoLinkBusy}
                    data-testid="auto-link-hour"
                  >
                    {Array.from({ length: 24 }, (_, h) => h).map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="al-look-back">Look-back days</Label>
                  <Input
                    id="al-look-back" type="number" min={1} max={180} step={1}
                    value={autoLink.look_back_days}
                    onChange={(e) => setAutoLink({ ...autoLink, look_back_days: Number(e.target.value) || 30 })}
                    disabled={autoLinkBusy}
                    data-testid="auto-link-lookback"
                  />
                </div>
                <div className="col-span-2 md:col-span-2">
                  <Label htmlFor="al-email" className="flex items-center gap-1">
                    <Bell size={11} /> Notify email (optional)
                  </Label>
                  <Input
                    id="al-email" type="email"
                    placeholder="bookkeeper@…"
                    value={autoLink.notify_email || ''}
                    onChange={(e) => setAutoLink({ ...autoLink, notify_email: e.target.value || null })}
                    disabled={autoLinkBusy}
                    data-testid="auto-link-email"
                  />
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <span className="text-[11px] text-muted-foreground">
                {autoLink?.updated_at && `Last saved ${String(autoLink.updated_at).slice(0, 10)} by ${autoLink.updated_by || 'admin'}.`}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline" className="rounded-xl"
                  disabled={autoLinkBusy || !autoLink?.enabled}
                  onClick={runAutoLinkNow}
                  data-testid="auto-link-run-now"
                >
                  <Play size={12} className="mr-1" />
                  {autoLinkBusy ? 'Running…' : 'Run now (test)'}
                </Button>
                <Button
                  size="sm" className="rounded-xl bg-sky-600 hover:bg-sky-700"
                  disabled={autoLinkBusy}
                  onClick={saveAutoLink}
                  data-testid="auto-link-save"
                >
                  {autoLinkBusy ? 'Saving…' : 'Save schedule'}
                </Button>
              </div>
            </div>
            {autoLinkRuns.length > 0 && (
              <details className="text-xs pt-1" data-testid="auto-link-runs-block">
                <summary className="cursor-pointer text-sky-700 font-medium">Recent runs ({autoLinkRuns.length})</summary>
                <ul className="mt-2 space-y-1">
                  {autoLinkRuns.map((r, i) => (
                    <li key={i} className="p-2 rounded bg-white/60 border border-sky-100" data-testid={`auto-link-run-${i}`}>
                      <div className="font-mono text-[11px] text-muted-foreground">
                        {String(r.ran_at).slice(0, 16).replace('T', ' ')} · {r.trigger} · scanned {r.scanned || 0}
                      </div>
                      <div className="text-[11px]">
                        {(r.accepted || []).length === 0
                          ? <span className="text-muted-foreground">Linked 0.</span>
                          : <span className="text-emerald-700">Linked <b>{(r.accepted || []).length}</b> · min score {r.min_score}%</span>}
                        {(r.failed || []).length > 0 && <span className="text-amber-700"> · Skipped {(r.failed || []).length}</span>}
                        {r.error && <span className="text-rose-700"> · {r.error}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </CardContent>
        </Card>

        {/* Review Queue — gov fees & salaries held for one-click approval */}
        {(queue?.rows?.length || 0) > 0 && (
          <Card className="border-emerald-300 bg-gradient-to-br from-emerald-50/70 to-teal-50/40" data-testid="review-queue-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between gap-2 flex-wrap">
                <span className="flex items-center gap-2">
                  <ShieldCheck size={15} className="text-emerald-600" />
                  Review Queue
                  <Badge className="bg-amber-100 text-amber-800 border-0">{queue.gov_fee} gov fees</Badge>
                  <Badge className="bg-blue-100 text-blue-800 border-0">{queue.salary} salaries</Badge>
                </span>
                <Button
                  size="sm"
                  className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={queueBusy}
                  onClick={() => approveQueueRows(queue.rows)}
                  data-testid="review-queue-approve-all-btn"
                >
                  {queueBusy ? <Loader2 size={13} className="animate-spin mr-1" /> : <CheckCircle2 size={13} className="mr-1" />}
                  Approve All ({queue.rows.length})
                </Button>
              </CardTitle>
              <p className="text-[11px] text-muted-foreground">
                SADAD / GOSI / ZAKAT fees and salary matches are held here instead of auto-posting. Approve to link them.
              </p>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="divide-y divide-emerald-100">
                {queue.rows.map((r) => (
                  <li key={`${r.statement_id}:${r.txn_index}`} className="py-1.5 flex items-center justify-between gap-2 text-xs" data-testid={`review-queue-row-${r.statement_id}-${r.txn_index}`}>
                    <div className="min-w-0 flex items-center gap-2">
                      <Badge className={`border-0 shrink-0 ${r.best.review_category === 'gov_fee' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                        {r.best.review_category === 'gov_fee' ? 'GOV FEE' : 'SALARY'}
                      </Badge>
                      <span className="font-mono text-muted-foreground shrink-0">{r.date}</span>
                      <span className="truncate" title={r.description}>{r.description}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-muted-foreground hidden sm:inline">→ {r.best.party_name} ({r.best.type.replace('_', ' ')})</span>
                      <span className="font-bold tabular-nums">{Number(r.amount).toLocaleString()}</span>
                      <Badge className="bg-emerald-100 text-emerald-800 border-0">{r.best.score}%</Badge>
                      <Button
                        size="sm" variant="outline"
                        className="h-6 px-2 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                        disabled={queueBusy}
                        onClick={() => approveQueueRows([r])}
                        data-testid={`review-queue-approve-btn-${r.statement_id}-${r.txn_index}`}
                      >
                        Approve
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Toolbar + status */}
        <Card className="border-violet-200 bg-gradient-to-br from-violet-50/60 to-fuchsia-50/40">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="space-y-0.5">
                <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                  <ListChecks size={14} className="text-violet-600" />
                  <span data-testid="rc-total-count">{data?.total ?? 0} unmatched txns</span>
                  <Badge className="bg-emerald-100 text-emerald-800 border-0">{totalWithBest} with candidate</Badge>
                  <Badge className="bg-amber-100 text-amber-800 border-0">{aboveThreshCount} ≥ {threshold}%</Badge>
                </div>
                {data?.summary && (
                  <div className="text-[11px] text-muted-foreground" data-testid="rc-summary">
                    In: {data.summary.credit_count} · SR {fmt(data.summary.credit_amount)} — Out: {data.summary.debit_count} · SR {fmt(data.summary.debit_amount)} — showing {rows.length} of {data.total}{!from && !to ? ' (all time)' : ''}
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground">
                  Confidence threshold (drag to change):
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="range" min={50} max={100} step={5}
                    value={threshold}
                    onChange={(e) => setThreshold(Number(e.target.value))}
                    className="accent-violet-600 w-44"
                    data-testid="rc-threshold"
                  />
                  <span className="text-xs font-mono w-10">{threshold}%</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <Button
                  size="sm" variant="outline" className="rounded-xl"
                  onClick={selectAllAboveThreshold}
                  disabled={loading || aboveThreshCount === 0}
                  data-testid="rc-select-above"
                >
                  Pick all ≥ {threshold}%
                </Button>
                <Button
                  size="sm" variant="outline" className="rounded-xl"
                  onClick={selectNone}
                  disabled={loading || selCount === 0}
                  data-testid="rc-select-none"
                >
                  Clear
                </Button>
                <Button
                  size="sm" className="rounded-xl bg-violet-600 hover:bg-violet-700"
                  onClick={acceptSelected}
                  disabled={busy || loading || selCount === 0}
                  data-testid="rc-accept-selected"
                >
                  <CheckCircle2 size={14} className="mr-1" />
                  {busy ? 'Linking…' : `Link ${selCount || ''} selected`}
                </Button>
              </div>
            </div>
            <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-violet-500 transition-all" style={{ width: `${Math.max(2, progressPct)}%` }} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              {progressPct}% of the loaded txns have a candidate. Re-run after each batch — newly-linked txns drop off the list automatically.
            </p>
          </CardContent>
        </Card>

        {/* Rows */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="animate-spin mr-2" size={16} /> Crunching candidates…
          </div>
        ) : rows.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="rc-empty">
              <CheckCircle2 className="mx-auto mb-2 text-emerald-500" size={28} />
              Nothing to reconcile in this range. Try widening the dates or removing the min-amount filter.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-3">
              <div className="divide-y divide-border max-h-[60dvh] overflow-y-auto pr-1" data-testid="rc-rows">
                {rows.map((r) => {
                  const key = `${r.statement_id}:${r.txn_index}`;
                  const best = r.best;
                  const isSel = !!selected[key];
                  const isCredit = r.kind === 'credit';
                  return (
                    <div
                      key={key}
                      className={`flex items-center gap-2 py-1.5 px-1 text-xs transition-colors ${isSel ? 'bg-violet-50/60' : 'hover:bg-muted/30'}`}
                      data-testid={`rc-row-${r.statement_id}-${r.txn_index}`}
                      title={`${r.description || ''}${r.reference ? ` · ref ${r.reference}` : ''}${r.beneficiary ? ` · ${r.beneficiary}` : ''}${r.statement_name ? ` · ${r.statement_name}` : ''}${best ? ` — ${best.why}` : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleRow(key)}
                        disabled={!best}
                        className="h-3.5 w-3.5 accent-violet-600 shrink-0"
                        data-testid={`rc-check-${r.statement_id}-${r.txn_index}`}
                      />
                      <span className="font-mono text-muted-foreground shrink-0 w-[74px]">{r.date}</span>
                      <span className="truncate flex-1 min-w-0 font-medium">{r.description || '(no description)'}</span>
                      <span className={`font-mono font-bold shrink-0 w-[100px] text-right ${isCredit ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {isCredit ? '↑' : '↓'} {Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      {best ? (
                        <span className="hidden sm:flex items-center gap-1.5 shrink-0 max-w-[260px] min-w-0">
                          <Badge variant="outline" className={`text-[9px] uppercase tracking-wide border ${TYPE_TONE[best.type] || ''}`}>{TYPE_LABEL[best.type] || best.type}</Badge>
                          <span className="truncate">{best.party_name}</span>
                          <Badge className={`text-[9px] border-0 ${best.score >= 90 ? 'bg-emerald-100 text-emerald-800' : best.score >= 65 ? 'bg-sky-100 text-sky-800' : 'bg-amber-100 text-amber-800'}`}>{Math.round(best.score)}%</Badge>
                        </span>
                      ) : (
                        <span className="hidden sm:flex items-center gap-1 text-[10px] text-amber-700 shrink-0"><AlertTriangle size={11} /> no candidate</span>
                      )}
                      <span className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 w-6 p-0 rounded-md"
                          onClick={() => setMatchDialog({ open: true, statementId: r.statement_id, txnIdx: r.txn_index })}
                          disabled={busy}
                          title="See all candidates"
                          data-testid={`rc-see-all-${r.statement_id}-${r.txn_index}`}
                        >
                          <RefreshCcw size={11} />
                        </Button>
                        {best && (
                          <Button
                            size="sm"
                            className="h-6 px-2 text-[10px] rounded-md bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => acceptOne(r)}
                            disabled={busy}
                            title="Accept this candidate"
                            data-testid={`rc-accept-${r.statement_id}-${r.txn_index}`}
                          >
                            <CheckCircle2 size={10} className="mr-0.5" /> Accept
                          </Button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
              {data?.has_more && (
                <div className="flex justify-center pt-3">
                  <Button size="sm" variant="outline" className="rounded-xl" onClick={loadMore} disabled={loadingMore} data-testid="rc-load-more">
                    {loadingMore ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
                    Load next {Math.min(PAGE_SIZE, data.total - rows.length)} ({rows.length} of {data.total} shown)
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <MatchCandidatesDialog
          open={matchDialog.open}
          onOpenChange={(o) => setMatchDialog((m) => ({ ...m, open: o }))}
          statementId={matchDialog.statementId}
          txnIdx={matchDialog.txnIdx}
          onMatched={load}
        />
      </div>
    </DashboardLayout>
  );
}

